import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SiteFilterProvider } from "@/contexts/SiteFilterContext";
import "./i18n/config";

// Lazy load all pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Temperature = lazy(() => import("./pages/Temperature"));
const Traceability = lazy(() => import("./pages/Traceability"));
const AgroTraceability = lazy(() => import("./pages/AgroTraceability"));
const GoodsReception = lazy(() => import("./pages/GoodsReception"));
const SecondaryDLC = lazy(() => import("./pages/SecondaryDLC"));
const Auth = lazy(() => import("./pages/Auth"));
const CompanyRegistration = lazy(() => import("./pages/CompanyRegistration"));
const ProductsInventory = lazy(() => import("./pages/ProductsInventory"));
const AITaskAgent = lazy(() => import("./pages/AITaskAgent"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const Settings = lazy(() => import("./pages/Settings"));
const CleaningPlans = lazy(() => import("./pages/CleaningPlans"));
const OilControl = lazy(() => import("./pages/OilControl"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SupervisorDashboard = lazy(() => import("./pages/SupervisorDashboard"));
const Archives = lazy(() => import("./pages/Archives"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Contact = lazy(() => import("./pages/Contact"));
const ExpertDirectory = lazy(() => import("./pages/ExpertDirectory"));
const ExpertRegistration = lazy(() => import("./pages/ExpertRegistration"));
const ExpertProfile = lazy(() => import("./pages/ExpertProfile"));
const ProviderOnboarding = lazy(() => import("./pages/ProviderOnboarding"));
const Laboratories = lazy(() => import("./pages/Laboratories"));
const LaboratoryDashboard = lazy(() => import("./pages/LaboratoryDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <SiteFilterProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/temperature" element={<Temperature />} />
              <Route path="/traceability" element={<Traceability />} />
              <Route path="/agro-traceability" element={<AgroTraceability />} /> {/* Region-gated in Layout nav; route kept for direct access */}
              <Route path="/goods-reception" element={<GoodsReception />} />
              <Route path="/secondary-dlc" element={<SecondaryDLC />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/company-registration" element={<CompanyRegistration />} />
              <Route path="/products-inventory" element={<ProductsInventory />} />
              <Route path="/ai-task-agent" element={<AITaskAgent />} />
              <Route path="/user-management" element={<UserManagement />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/cleaning-plans" element={<CleaningPlans />} />
              <Route path="/oil-control" element={<OilControl />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/supervisor" element={<SupervisorDashboard />} />
              <Route path="/archives" element={<Archives />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/experts" element={<ExpertDirectory />} />
              <Route path="/expert-directory" element={<ExpertDirectory />} />
              <Route path="/expert-registration" element={<ExpertRegistration />} />
              <Route path="/provider-onboarding" element={<ProviderOnboarding />} />
              <Route path="/expert/:id" element={<ExpertProfile />} />
              <Route path="/laboratories" element={<Laboratories />} />
              <Route path="/laboratory-dashboard" element={<LaboratoryDashboard />} />
              {/* Redirects for old routes */}
              <Route path="/reports" element={<Settings />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </SiteFilterProvider>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
